import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  AdminAuditLogEntry,
  AdminDashboardSummary,
  AdminUserDetail,
  AppConfigEntry,
  ApiEnvelope,
  User,
} from "@/lib/api-types";

/**
 * Admin hooks -- SUPER_ADMIN-only surfaces. Every query/mutation mirrors a
 * route in backend/functions/staxed_admin_handler/index.mjs. The backend
 * re-checks the SUPER_ADMIN role inside each handler, so a non-admin token
 * yields a 403 (surfaced as a toast by the api interceptor); the UI also
 * gates the whole section in AdminLayout as a first line of defence.
 */

export type AdminUserFilters = {
  /** Defaults to STAXED server-side when omitted. */
  org_id?: string;
  user_status?: User["user_status"];
  role_id?: User["role_id"];
  /** Sign-up date order. Server default: created_desc (newest first). */
  sort?: "created_desc" | "created_asc";
  limit?: number;
};

export type AdminAuditFilters = {
  /** actor_user_id */
  actor?: string;
  /** target_resource_id */
  target?: string;
  limit?: number;
};

/** GET /admin/dashboard -> platform stat rollup. */
export function useAdminDashboard(enabled = true) {
  return useQuery({
    queryKey: qk.admin.dashboard(),
    enabled,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<AdminDashboardSummary>>("/admin/dashboard");
      return unwrap(res.data);
    },
    staleTime: 30_000,
  });
}

/** GET /admin/users -> User[]. Filters: org_id / user_status / role_id / sort / limit. */
export function useAdminUsers(filters: AdminUserFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...qk.admin.users(), filters] as const,
    enabled,
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.org_id) params.org_id = filters.org_id;
      if (filters.user_status) params.user_status = filters.user_status;
      if (filters.role_id) params.role_id = filters.role_id;
      if (filters.sort) params.sort = filters.sort;
      if (filters.limit) params.limit = filters.limit;
      const res = await api.get<ApiEnvelope<User[]>>("/admin/users", { params });
      return unwrap(res.data) ?? [];
    },
    staleTime: 15_000,
  });
}

/** GET /admin/users/{user_id} -> { user, subscriptions, recent_payments }. */
export function useAdminUserDetail(userId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.admin.user(userId ?? ""),
    enabled: enabled && !!userId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<AdminUserDetail>>(
        `/admin/users/${encodeURIComponent(userId!)}`,
      );
      return unwrap(res.data);
    },
    staleTime: 15_000,
  });
}

/** POST /admin/users/{user_id}/status -> { user_id, status }. */
export function useSetUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { user_id: string; status: User["user_status"] }) => {
      const res = await api.post<ApiEnvelope<{ user_id: string; status: string }>>(
        `/admin/users/${encodeURIComponent(vars.user_id)}/status`,
        { status: vars.status },
      );
      return unwrap(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.admin.user(vars.user_id) });
      qc.invalidateQueries({ queryKey: qk.admin.users() });
      qc.invalidateQueries({ queryKey: qk.admin.dashboard() });
    },
  });
}

/** POST /admin/users/{user_id}/role -> { user_id, role_id }. */
export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { user_id: string; role_id: User["role_id"] }) => {
      const res = await api.post<ApiEnvelope<{ user_id: string; role_id: string }>>(
        `/admin/users/${encodeURIComponent(vars.user_id)}/role`,
        { role_id: vars.role_id },
      );
      return unwrap(res.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.admin.user(vars.user_id) });
      qc.invalidateQueries({ queryKey: qk.admin.users() });
    },
  });
}

/** GET /admin/audit-logs -> AdminAuditLogEntry[]. Filters: actor / target / limit. */
export function useAdminAuditLogs(filters: AdminAuditFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...qk.admin.audit(), filters] as const,
    enabled,
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.actor) params.actor = filters.actor;
      if (filters.target) params.target = filters.target;
      if (filters.limit) params.limit = filters.limit;
      const res = await api.get<ApiEnvelope<AdminAuditLogEntry[]>>("/admin/audit-logs", {
        params,
      });
      return unwrap(res.data) ?? [];
    },
    staleTime: 10_000,
  });
}

/**
 * GET /admin/config/{key} -> AppConfigEntry. The handler 404s when the key
 * has never been written; we treat that as "no value yet" (data = null)
 * rather than an error so the config page can render its create form.
 */
export function useAdminConfig(key: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.admin.config(key ?? ""),
    enabled: enabled && !!key,
    retry: false,
    queryFn: async () => {
      try {
        const res = await api.get<ApiEnvelope<AppConfigEntry>>(
          `/admin/config/${encodeURIComponent(key!)}`,
        );
        return unwrap(res.data) ?? null;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

/** POST /admin/config/{key} -> AppConfigEntry. */
export function useSetAdminConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      key: string;
      value: string | number | boolean;
      value_type?: AppConfigEntry["value_type"];
      description?: string;
    }) => {
      const body: Record<string, unknown> = { value: vars.value };
      if (vars.value_type) body.value_type = vars.value_type;
      if (vars.description !== undefined) body.description = vars.description;
      const res = await api.post<ApiEnvelope<AppConfigEntry>>(
        `/admin/config/${encodeURIComponent(vars.key)}`,
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(qk.admin.config(vars.key), data ?? null);
      qc.invalidateQueries({ queryKey: qk.admin.config(vars.key) });
    },
  });
}
